// src/pages/PlanesPage.jsx
import React from 'react';
import { useState, useEffect, useRef, useMemo } from 'react';
import axiosInstance from '../axiosInstance';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { useClinic } from '../context/ClinicContext';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Paper,
  Fade,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Tooltip,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TextField,
  InputAdornment,
  Slide
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { motion } from 'framer-motion';
import n1coLogo from '../assets/logo-n1co.png';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckIcon from '@mui/icons-material/Check';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import SecurityIcon from '@mui/icons-material/Security';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import LockIcon from '@mui/icons-material/Lock';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const MotionPaper = motion(Paper);

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const headerGradient = (t) =>
  `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.primary.dark} 100%)`;

const cardHeaderGradient = (t) =>
  `linear-gradient(135deg, ${alpha(t.palette.primary.main, 0.95)} 0%, ${alpha(t.palette.primary.dark, 0.9)} 100%)`;

const tonedHover = (t) =>
  t.palette.mode === 'light'
    ? alpha(t.palette.primary.main, 0.03)
    : alpha(t.palette.primary.light, 0.06);

// ==================== MODAL DE PAGO N1CO ====================
const N1COPaymentModal = ({ open, plan, onClose, onSuccess }) => {
  const theme = useTheme();
  const [step, setStep] = useState('card');
  const [cardData, setCardData] = useState({
    number: '',
    holder: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: ''
  });
  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState('');
  const [authenticationUrl, setAuthenticationUrl] = useState('');
  const [authenticationId, setAuthenticationId] = useState('');
  const [paymentMethodId, setPaymentMethodId] = useState('');
  const [subscriptionId, setSubscriptionId] = useState('');

  useEffect(() => {
    if (open) {
      setStep('card');
      setCardData({ number: '', holder: '', expiryMonth: '', expiryYear: '', cvv: '' });
      setErrors({});
      setErrorMessage('');
      setAuthenticationUrl('');
      setAuthenticationId('');
      setPaymentMethodId('');
      setSubscriptionId('');
    }
  }, [open]);

  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    return parts.length ? parts.join(' ') : value;
  };

  const getCardType = (number) => {
    const n = number.replace(/\s/g, '');
    if (/^4/.test(n)) return 'visa';
    if (/^5[1-5]/.test(n)) return 'mastercard';
    if (/^3[47]/.test(n)) return 'amex';
    if (/^6(?:011|5)/.test(n)) return 'discover';
    return null;
  };

  const validateCard = () => {
    const newErrors = {};
    const cleanNumber = cardData.number.replace(/\s/g, '');
    if (cleanNumber.length < 13 || cleanNumber.length > 19) newErrors.number = 'Número inválido';
    if (!cardData.holder || cardData.holder.length < 3) newErrors.holder = 'Nombre requerido';
    const month = parseInt(cardData.expiryMonth);
    if (!month || month < 1 || month > 12) newErrors.expiryMonth = 'Mes inválido';
    const year = parseInt(cardData.expiryYear);
    const currentYear = new Date().getFullYear() % 100;
    if (!year || year < currentYear || year > currentYear + 20) newErrors.expiryYear = 'Año inválido';
    if (!cardData.cvv || cardData.cvv.length < 3) newErrors.cvv = 'CVV inválido';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'number') {
      setCardData(prev => ({ ...prev, [name]: formatCardNumber(value) }));
    } else if (name === 'expiryMonth') {
      setCardData(prev => ({ ...prev, [name]: value.replace(/\D/g, '').slice(0, 2) }));
    } else if (name === 'expiryYear') {
      setCardData(prev => ({ ...prev, [name]: value.replace(/\D/g, '').slice(0, 2) }));
    } else if (name === 'cvv') {
      setCardData(prev => ({ ...prev, [name]: value.replace(/\D/g, '').slice(0, 4) }));
    } else {
      setCardData(prev => ({ ...prev, [name]: value.toUpperCase() }));
    }
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const createSubscription = async (paymentMethodIdToUse) => {
    setStep('processing');
    try {
      const response = await axiosInstance.post('/n1co/iniciar-suscripcion/', {
        plan_id: plan.id,
        payment_method_id: paymentMethodIdToUse
      });

      const data = response.data;
      console.log('N1CO iniciar response:', data);

      if (data.status === 'SUCCEEDED') {
        setStep('success');
        toast.success('¡Suscripción activada correctamente!');
        if (onSuccess) setTimeout(() => onSuccess(data), 1500);

      } else if (data.status === 'AUTHENTICATION_REQUIRED') {
        setSubscriptionId(data.subscription_id || '');
        setAuthenticationUrl(
          data.authentication_url ||
          data.authentication?.url ||
          ''
        );
        setAuthenticationId(
          data.authentication_id ||
          data.authentication?.id ||
          ''
        );
        setPaymentMethodId(paymentMethodIdToUse);
        setStep('3ds');

      } else if (!data.success) {
        throw new Error(data.error || 'Error al crear suscripción');

      } else {
        setStep('success');
        toast.success('¡Suscripción activada correctamente!');
        if (onSuccess) setTimeout(() => onSuccess(data), 1500);
      }

    } catch (error) {
      console.error('Error al crear suscripción:', error);
      setStep('error');
      setErrorMessage(
        error.response?.data?.error ||
        error.message ||
        'Error al procesar la suscripción'
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateCard()) return;

    setStep('processing');
    setErrorMessage('');

    try {
      const tokenResponse = await axiosInstance.post('/n1co/tokenizar-tarjeta/', {
        card_number: cardData.number.replace(/\s/g, ''),
        card_holder: cardData.holder,
        expiry_month: cardData.expiryMonth,
        expiry_year: cardData.expiryYear,
        cvv: cardData.cvv
      });

      if (!tokenResponse.data.success) {
        throw new Error(tokenResponse.data.error || 'Error al tokenizar tarjeta');
      }

      const paymentMethodToken = tokenResponse.data.id;
      if (!paymentMethodToken) {
        throw new Error('No se recibió el token de la tarjeta');
      }

      await createSubscription(paymentMethodToken);

    } catch (error) {
      console.error('Error en el pago:', error);
      setStep('error');
      setErrorMessage(
        error.response?.data?.error ||
        error.message ||
        'Error al procesar el pago'
      );
    }
  };

  // Verifica la suscripción con N1CO tras el 3DS.
  // Acepta opcionalmente el authenticationId que devuelve el postMessage del iframe
  // (N1CO puede generar un nuevo authenticationId al completar la autenticación).
  const verificarSuscripcion = async (authIdFinal) => {
    setStep('processing');
    try {
      const payload = {
        plan_id: plan.id,
        payment_method_id: paymentMethodId,
        authentication_id: authIdFinal || authenticationId,
      };

      if (subscriptionId) {
        payload.subscription_id = subscriptionId;
      }

      console.log('N1CO verificar payload:', payload);

      const response = await axiosInstance.post(
        '/n1co/verificar-suscripcion/',
        payload,
        { timeout: 30000 }
      );

      if (response.data.success && response.data.status === 'SUCCEEDED') {
        setStep('success');
        toast.success('¡Suscripción activada correctamente!');
        if (onSuccess) {
          setTimeout(() => {
            onSuccess(response.data);
            onClose();
            window.location.reload();
          }, 2000);
        }
      } else {
        setStep('error');
        setErrorMessage(response.data.error || 'Error al verificar la autenticación');
      }
    } catch (error) {
      console.error('Error verificando 3DS:', error);
      setStep('error');
      setErrorMessage(
        error.response?.data?.error ||
        error.message ||
        'Error al verificar'
      );
    }
  };

  // Botón manual de respaldo (por si el postMessage no llega).
  const handle3DSComplete = () => {
    verificarSuscripcion(authenticationId);
  };

  // Escucha el postMessage del iframe de N1CO mientras estamos en el paso 3DS.
  // N1CO envía { MessageType, Status, AuthenticationId } cuando el banco termina.
  useEffect(() => {
    if (step !== '3ds') return;

    const handleMessage = (event) => {
      // El postMessage puede llegar como objeto o como string JSON.
      let data = event.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch (e) {
          // No es JSON; lo ignoramos (puede ser ruido de otros iframes).
          return;
        }
      }
      if (!data || typeof data !== 'object') return;

      // Algunos proveedores anidan el payload (ej: data.data o data.message).
      const payload = data.data && typeof data.data === 'object' ? data.data : data;

      const messageType = payload.MessageType || payload.messageType || payload.type;
      const statusMsg = payload.Status || payload.status;
      const newAuthId =
        payload.AuthenticationId ||
        payload.authenticationId ||
        payload.AuthenticationID ||
        payload.id;

      // LOG de TODO lo que llega para diagnosticar (incluye el origin).
      console.log('N1CO 3DS postMessage RAW:', { origin: event.origin, data });

      if (!messageType && !statusMsg) return;

      const status = String(statusMsg || '').toUpperCase();
      const mType = String(messageType || '').toLowerCase();

      if (mType === 'authentication.complete' || status === 'SUCCESS' || status === 'SUCCEEDED') {
        if (status === 'SUCCESS' || status === 'SUCCEEDED' || mType === 'authentication.complete') {
          // Éxito: disparamos la verificación automáticamente.
          verificarSuscripcion(newAuthId || authenticationId);
        }
      } else if (
        mType === 'authentication.failed' ||
        status === 'FAILED' ||
        status === 'ERROR'
      ) {
        setStep('error');
        setErrorMessage('La autenticación con tu banco falló. Intenta con otra tarjeta.');
      } else if (status === 'EXPIRED') {
        setStep('error');
        setErrorMessage('La sesión de autenticación expiró. Intenta de nuevo.');
      }
      // PENDING: seguimos esperando.
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, authenticationId, paymentMethodId, subscriptionId]);

  const cardType = getCardType(cardData.number);

  const renderContent = () => {
    if (step === 'success') {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Avatar sx={{ bgcolor: 'success.main', width: 72, height: 72, mx: 'auto', mb: 2 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography variant="h5" fontWeight={700} color="success.main" gutterBottom>
            ¡Suscripción Exitosa!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Tu plan <strong>{plan?.nombre}</strong> ha sido activado.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Se cobrarán <strong>${plan?.precio}</strong> de forma recurrente.
          </Typography>
        </Box>
      );
    }

    if (step === 'error') {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Avatar sx={{ bgcolor: 'error.main', width: 72, height: 72, mx: 'auto', mb: 2 }}>
            <WarningAmberIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography variant="h5" fontWeight={700} color="error.main" gutterBottom>
            Error en el Pago
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {errorMessage}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button variant="contained" onClick={() => setStep('card')}>
              Intentar de Nuevo
            </Button>
            <Button variant="outlined" onClick={onClose}>
              Cancelar
            </Button>
          </Box>
        </Box>
      );
    }

    if (step === '3ds') {
      return (
        <Box sx={{ textAlign: 'center', py: 1 }}>
          <VerifiedUserIcon sx={{ fontSize: 44, color: 'warning.main', mb: 1 }} />
          <Typography variant="h6" gutterBottom fontWeight={700}>
            Autenticación 3DS Requerida
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Tu banco requiere verificación adicional. Completa la autenticación en
            el recuadro de abajo. La ventana se cerrará automáticamente al terminar.
          </Typography>

          {authenticationUrl ? (
            <Box
              sx={{
                width: '100%',
                height: 420,
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden',
                mb: 2
              }}
            >
              <iframe
                title="Autenticación 3D Secure"
                src={authenticationUrl}
                width="100%"
                height="100%"
                style={{ border: 'none' }}
              />
            </Box>
          ) : (
            <CircularProgress sx={{ my: 4 }} />
          )}

          <Divider sx={{ mb: 2 }} />

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            ¿Ya completaste la verificación y no avanza? Pulsa aquí.
          </Typography>
          <Button variant="outlined" size="small" onClick={handle3DSComplete}>
            Ya completé la autenticación ✓
          </Button>
        </Box>
      );
    }

    if (step === 'processing') {
      return (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress size={56} thickness={4} />
          <Typography variant="h6" sx={{ mt: 3, color: 'text.secondary' }}>
            Procesando pago...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Por favor espera, no cierres esta ventana.
          </Typography>
        </Box>
      );
    }

    return (
      <Box component="form" onSubmit={handleSubmit}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            border: 1,
            borderColor: alpha(theme.palette.primary.main, 0.2)
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>{plan?.nombre}</Typography>
              <Typography variant="body2" color="text.secondary">Cobro automático recurrente</Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="h5" fontWeight={800} color="primary.main">${plan?.precio}</Typography>
              <Typography variant="caption" color="text.secondary">/mes</Typography>
            </Box>
          </Box>
        </Paper>

        {cardType && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Chip
              label={cardType.toUpperCase()}
              size="small"
              sx={{
                fontWeight: 700,
                bgcolor:
                  cardType === 'visa' ? '#1a1f71' :
                  cardType === 'mastercard' ? '#eb001b' :
                  cardType === 'amex' ? '#006fcf' : '#ff6000',
                color: 'white'
              }}
            />
          </Box>
        )}

        <TextField
          fullWidth
          label="Número de tarjeta"
          name="number"
          value={cardData.number}
          onChange={handleChange}
          placeholder="1234 5678 9012 3456"
          error={!!errors.number}
          helperText={errors.number}
          inputProps={{ maxLength: 19 }}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <CreditCardIcon color="action" />
              </InputAdornment>
            )
          }}
        />

        <TextField
          fullWidth
          label="Nombre del titular"
          name="holder"
          value={cardData.holder}
          onChange={handleChange}
          placeholder="JUAN PÉREZ"
          error={!!errors.holder}
          helperText={errors.holder}
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <TextField
            label="Mes"
            name="expiryMonth"
            value={cardData.expiryMonth}
            onChange={handleChange}
            placeholder="MM"
            error={!!errors.expiryMonth}
            helperText={errors.expiryMonth}
            inputProps={{ maxLength: 2, style: { textAlign: 'center' } }}
            sx={{ flex: 1 }}
          />
          <TextField
            label="Año"
            name="expiryYear"
            value={cardData.expiryYear}
            onChange={handleChange}
            placeholder="AA"
            error={!!errors.expiryYear}
            helperText={errors.expiryYear}
            inputProps={{ maxLength: 2, style: { textAlign: 'center' } }}
            sx={{ flex: 1 }}
          />
          <TextField
            label="CVV"
            name="cvv"
            type="password"
            value={cardData.cvv}
            onChange={handleChange}
            placeholder="***"
            error={!!errors.cvv}
            helperText={errors.cvv}
            inputProps={{ maxLength: 4, style: { textAlign: 'center' } }}
            sx={{ flex: 1 }}
          />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            startIcon={<LockIcon />}
            sx={{ py: 1.5, borderRadius: 2, fontWeight: 700, fontSize: '1rem' }}
          >
            Suscribirse
          </Button>
          <Button fullWidth variant="text" onClick={onClose} sx={{ color: 'text.secondary' }}>
            Cancelar
          </Button>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 3, color: 'text.secondary' }}>
          <LockIcon fontSize="small" />
          <Typography variant="caption">
            Tus datos están protegidos con encriptación SSL
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={step === 'processing' ? undefined : onClose}
      maxWidth={step === '3ds' ? 'md' : 'sm'}
      fullWidth
      TransitionComponent={Transition}
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pb: step === 'success' ? 0 : 1
      }}>
        <Box component="span" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
          {step === 'success' ? '' : 'Método de Pago'}
        </Box>
        {step !== 'processing' && step !== 'success' && (
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      {step !== 'success' && (
        <Paper
          elevation={0}
          sx={{
            mx: 3,
            mt: 2,
            p: 1.5,
            backgroundColor: '#f0f7ff',
            border: '1px solid #b3d4f5',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VerifiedUserIcon sx={{ color: '#1976d2', fontSize: 20 }} />
            <Box>
              <Typography variant="caption" sx={{ color: '#1976d2', fontWeight: 700, display: 'block' }}>
                Pagos procesados por
              </Typography>
              <Box
                component="a"
                href="https://n1co.shop"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: 'flex', alignItems: 'center', gap: 1, textDecoration: 'none' }}
              >
                <Box component="img" src={n1coLogo} alt="N1CO" sx={{ height: 28, objectFit: 'contain' }} />
                <OpenInNewIcon sx={{ fontSize: 12, color: '#0d47a1' }} />
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LockIcon sx={{ color: '#2e7d32', fontSize: 16 }} />
            <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 600 }}>
              Conexión SSL 256-bit
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <SecurityIcon sx={{ color: '#6a1b9a', fontSize: 16 }} />
            <Typography variant="caption" sx={{ color: '#6a1b9a', fontWeight: 600 }}>
              PCI DSS Compliant
            </Typography>
          </Box>
        </Paper>
      )}

      {step !== 'success' && (
        <Box sx={{
          mt: 2,
          pt: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1
        }}>
          <LockIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary" textAlign="center">
            Tus datos van directamente a{' '}
            <Box
              component="a"
              href="https://n1co.shop"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: 'primary.main', fontWeight: 600, textDecoration: 'none' }}
            >
              N1CO
            </Box>
            {' '}y nunca son almacenados en nuestros servidores.
          </Typography>
        </Box>
      )}

      <DialogContent sx={{ px: 3, pb: 3 }}>
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
};

// ==================== COMPONENTE PRINCIPAL ====================
const PlanesPage = () => {
  const theme = useTheme();
  const [planes, setPlanes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fadeIn, setFadeIn] = useState(false);
  const [error, setError] = useState('');
  const [isThrottled, setIsThrottled] = useState(false);
  const [throttleRemaining, setThrottleRemaining] = useState(0);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const { user, suscripcion, refreshUser } = useAuth();
  const { selectedClinicId } = useClinic();

  const retryTimeoutRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const planesSectionRef = useRef(null);

  const mostrarPlanesClinicaEnEstaPagina = false;

  const caracteristicasPorPlan = useMemo(() => ({
    'Plan Básico': [
      'Hasta 30 pacientes',
      'Acceso a funcionalidades básicas',
      'Telemedicina',
      'Soporte por correo electrónico',
      'Asistente IA',
    ],
    'Plan Premium Mensual': [
      'Hasta 200 pacientes',
      'Todas las funcionalidades básicas',
      'Reportes en PDF',
      'Receta Médica Electrónica',
      'Finanzas',
      'Recordatorios automáticos',
      'Telemedicina',
      'Asistente IA',
      'Actualizaciones',
      'Soporte prioritario',
    ],
    'Plan Premium Anual': [
      'Pacientes ilimitados',
      'Todas las funcionalidades avanzadas',
      'Reportes en PDF',
      'Receta Médica Electrónica',
      'Finanzas',
      'Recordatorios automáticos',
      'Telemedicina',
      'Asistente IA',
      'Actualizaciones',
      'Soporte 24/7',
      'Descuento 25% vs mensual',
    ],
  }), []);

  const esPlanIndividual = (plan) => Boolean(caracteristicasPorPlan[plan?.nombre]);

  const clearTimers = () => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  };

  const parseThrottleWaitSeconds = (err) => {
    const header = err?.response?.headers?.['retry-after'];
    if (header) {
      const s = parseInt(header, 10);
      if (!Number.isNaN(s) && s > 0) return s;
    }
    const detail = err?.response?.data?.detail || '';
    const m = String(detail).match(/(\d+)\s*seg/);
    if (m && m[1]) return parseInt(m[1], 10);
    return 30;
  };

  const startThrottleCountdown = (seconds, refetchFn) => {
    setIsThrottled(true);
    setThrottleRemaining(seconds);
    countdownIntervalRef.current = setInterval(() => {
      setThrottleRemaining((prev) => {
        if (prev <= 1) {
          clearTimers();
          refetchFn();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const endpoint = useMemo(() => {
    if (selectedClinicId) {
      if (!mostrarPlanesClinicaEnEstaPagina) return null;
      return '/clinica/planes/';
    }
    return '/planes/';
  }, [selectedClinicId, mostrarPlanesClinicaEnEstaPagina]);

  const fetchPlanes = async (signal) => {
    setLoading(true);
    setError('');
    setIsThrottled(false);
    clearTimers();

    try {
      if (!endpoint) {
        setPlanes([]);
        setFadeIn(true);
        setLoading(false);
        return;
      }
      const res = await axiosInstance.get(endpoint, { signal });
      const data = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
      setPlanes(data);
      setTimeout(() => setFadeIn(true), 80);
    } catch (e) {
      if (
        e?.name === 'CanceledError' ||
        e?.name === 'AbortError' ||
        e?.code === 'ERR_CANCELED'
      ) {
        return;
      }
      const status = e?.response?.status;
      if (status === 429) {
        const waitSec = parseThrottleWaitSeconds(e);
        setLoading(false);
        setError('');
        startThrottleCountdown(waitSec, () => {
          const controller = new AbortController();
          fetchPlanes(controller.signal);
        });
        return;
      }
      const msg =
        e?.response?.data?.detail ||
        e?.response?.data?.error ||
        'No se pudieron cargar los planes.';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchPlanes(controller.signal);
    return () => {
      controller.abort();
      clearTimers();
    };
  }, [endpoint]);

  const handleRetryNow = () => {
    clearTimers();
    const controller = new AbortController();
    fetchPlanes(controller.signal);
  };

  const handleMejorarPlan = (plan) => {
    setSelectedPlan(plan);
    setPaymentModalOpen(true);
  };

  const handlePaymentSuccess = async (data) => {
    setPaymentModalOpen(false);
    if (refreshUser) {
      await refreshUser();
    }
  };

  const scrollToPlanes = () => {
    if (planesSectionRef.current) {
      planesSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const usuarioTienePlanActivo = Boolean(suscripcion?.plan && suscripcion?.estado === 'Activa');
  const mostrandoClinicaPeroOculto = Boolean(selectedClinicId && !mostrarPlanesClinicaEnEstaPagina);

  const planesDisponibles = useMemo(() => {
    if (mostrandoClinicaPeroOculto) return [];
    if (!planes || planes.length === 0) return [];

    if (!selectedClinicId) {
      const individuales = planes.filter(esPlanIndividual);
      if (usuarioTienePlanActivo) {
        return individuales.filter(
          (p) => parseFloat(p.precio) > parseFloat(suscripcion.plan.precio)
        );
      }
      return individuales;
    }

    return planes;
  }, [
    planes,
    usuarioTienePlanActivo,
    suscripcion?.plan?.precio,
    selectedClinicId,
    mostrandoClinicaPeroOculto,
  ]);

  const fechaFinLegible = suscripcion?.fecha_fin
    ? new Date(suscripcion.fecha_fin).toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })
    : '—';

  const hayPlanesParaMostrar = planesDisponibles.length > 0;

  if (loading) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress size={56} thickness={4} />
        <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
          Cargando planes disponibles...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: { xs: 3, md: 4 } }}>
      <N1COPaymentModal
        open={paymentModalOpen}
        plan={selectedPlan}
        onClose={() => setPaymentModalOpen(false)}
        onSuccess={handlePaymentSuccess}
      />

      {/* HEADER */}
      <MotionPaper
        initial={{ opacity: 0, y: -18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        elevation={0}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 4,
          mb: 3,
          mx: { xs: 2, md: 3 },
          px: { xs: 3, md: 5 },
          py: { xs: 3, md: 4 },
          background: (t) => headerGradient(t),
          color: 'white',
        }}
      >
        <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: alpha('#fff', 0.12) }} />
        <Box sx={{ position: 'absolute', bottom: -30, left: -30, width: 150, height: 150, borderRadius: '50%', background: alpha('#fff', 0.09) }} />

        <Grid container spacing={2} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid item xs={12} sm="auto">
            <Box sx={{
              bgcolor: alpha('#fff', 0.2),
              color: 'white',
              borderRadius: '50%',
              width: 60,
              height: 60,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 8px 32px ${alpha('#000', 0.2)}`,
              mx: { xs: 'auto', sm: 0 }
            }}>
              <MedicalServicesIcon sx={{ fontSize: 34 }} />
            </Box>
          </Grid>

          <Grid item xs={12} sm>
            <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
              <Typography variant="h4" sx={{ fontWeight: 800, textShadow: '0 2px 10px rgba(0,0,0,0.25)' }}>
                Nuestros Planes
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.95 }}>
                Elige el plan ideal para tu práctica y potencia tu consulta
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm="auto">
            <Tooltip title={hayPlanesParaMostrar ? 'Ver los planes disponibles' : 'No hay planes disponibles'}>
              <span>
                <Button
                  variant="contained"
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={scrollToPlanes}
                  disabled={!hayPlanesParaMostrar}
                  sx={{
                    bgcolor: 'white',
                    color: 'primary.main',
                    fontWeight: 'bold',
                    borderRadius: 3,
                    px: 3,
                    py: 1.2,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                    '&:hover': {
                      bgcolor: 'grey.100',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 25px rgba(0,0,0,0.2)'
                    },
                    '&:disabled': {
                      bgcolor: alpha('#fff', 0.6),
                      color: alpha(theme.palette.primary.main, 0.5),
                    },
                    transition: 'all 0.25s ease',
                  }}
                >
                  Explorar Planes
                </Button>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      </MotionPaper>

      <Box sx={{ p: { xs: 2.5, sm: 3.5, md: 4 }, pt: { xs: 1.5, sm: 2 }, maxWidth: '1600px', mx: 'auto' }}>

        {isThrottled && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2, boxShadow: theme.shadows[2] }}>
            Has alcanzado el límite de solicitudes. Reintentaremos automáticamente en {throttleRemaining}s.
            <Button onClick={handleRetryNow} sx={{ ml: 2 }} size="small" variant="outlined">
              Reintentar ahora
            </Button>
          </Alert>
        )}

        {error && !isThrottled && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2, boxShadow: theme.shadows[2] }}>
            {error}
            <Button onClick={handleRetryNow} sx={{ ml: 2 }} size="small" variant="outlined">
              Reintentar
            </Button>
          </Alert>
        )}

        <Fade in={fadeIn} timeout={800}>
          <Box>
            {mostrandoClinicaPeroOculto && (
              <Alert severity="info" sx={{ mb: 3, borderRadius: 2, boxShadow: theme.shadows[1] }}>
                Estás en modo clínica. Los planes de clínica se gestionan en "Suscripción (Clínica)".
              </Alert>
            )}

            {!selectedClinicId && usuarioTienePlanActivo && (
              <Fade in={true} timeout={1200}>
                <Paper
                  elevation={0}
                  variant="outlined"
                  sx={{
                    p: { xs: 2, md: 3 },
                    mb: 5,
                    borderRadius: 3,
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    transition: 'transform .25s ease, box-shadow .25s ease',
                    '&:hover': { transform: 'translateY(-2px)', boxShadow: theme.shadows[4] }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'success.main', width: 56, height: 56 }}>
                      <CheckCircleOutlineIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h5" fontWeight={700}>
                        ¡Felicidades, Dr. {user?.first_name ? `${user.first_name} ${user.last_name || ''}` : user?.username}!
                      </Typography>
                      <Typography variant="body1" sx={{ mt: 0.5 }}>
                        Actualmente tienes el <b>{suscripcion.plan.nombre}</b>
                      </Typography>
                    </Box>
                  </Box>

                  <Chip
                    icon={<AccessTimeIcon />}
                    label={
                      <Box component="span" sx={{ display: 'inline-flex', flexDirection: 'column', textAlign: 'left' }}>
                        <Typography variant="caption" sx={{ lineHeight: 1 }}>Vence el</Typography>
                        <Typography variant="subtitle1" sx={{ lineHeight: 1.1, fontWeight: 700 }}>{fechaFinLegible}</Typography>
                      </Box>
                    }
                    color="success"
                    variant="filled"
                    sx={{
                      py: 2,
                      px: 1.5,
                      borderRadius: 2,
                      backgroundColor: theme.palette.mode === 'light'
                        ? 'rgba(46,125,50,0.08)'
                        : 'rgba(102,187,106,0.15)'
                    }}
                  />
                </Paper>
              </Fade>
            )}

            <Box ref={planesSectionRef}>
              <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} justifyContent="center" alignItems="stretch">
                {hayPlanesParaMostrar ? (
                  planesDisponibles.map((plan, idx) => {
                    const isPopular = plan?.nombre?.toLowerCase().includes('premium');
                    return (
                      <Grid item key={plan.id ?? `${plan.nombre}-${idx}`} xs={12} sm={6} md={4}>
                        <Fade in={fadeIn} timeout={1000 + idx * 180}>
                          <Card
                            role="article"
                            aria-label={`Plan ${plan.nombre}`}
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              height: '100%',
                              borderRadius: 3,
                              overflow: 'hidden',
                              boxShadow: theme.shadows[2],
                              transition: 'transform .25s ease, box-shadow .25s ease, background-color .25s ease',
                              outline: '1px solid',
                              outlineColor: theme.palette.mode === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.08)',
                              '&:hover': {
                                transform: 'translateY(-6px)',
                                boxShadow: theme.shadows[8],
                                backgroundColor: tonedHover(theme)
                              },
                              '&:focus-within': { boxShadow: theme.shadows[10] }
                            }}
                          >
                            <Box sx={{
                              position: 'relative',
                              p: 3,
                              textAlign: 'center',
                              color: 'primary.contrastText',
                              background: (t) => cardHeaderGradient(t),
                            }}>
                              <Typography variant="h5" fontWeight={800} letterSpacing=".2px">
                                {plan.nombre}
                              </Typography>
                              {isPopular && (
                                <Chip
                                  size="small"
                                  label="Más popular"
                                  color="secondary"
                                  sx={{
                                    position: 'absolute',
                                    top: 12,
                                    right: 12,
                                    fontWeight: 700,
                                    bgcolor: (t) => alpha(t.palette.secondary.main, 0.9),
                                    color: (t) => t.palette.secondary.contrastText
                                  }}
                                />
                              )}
                            </Box>

                            <CardContent sx={{ flexGrow: 1, p: { xs: 2.5, md: 3 } }}>
                              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'baseline', my: 2.5, gap: 1 }}>
                                <Typography
                                  variant="h3"
                                  color="primary.main"
                                  fontWeight={800}
                                  sx={{ fontSize: { xs: '2.1rem', md: '2.4rem' } }}
                                >
                                  ${parseFloat(plan.precio).toFixed(2)}
                                </Typography>
                                <Typography variant="body1" color="text.secondary">/mes</Typography>
                              </Box>

                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2.5 }}>
                                <CalendarMonthIcon color="action" sx={{ mr: 0.25 }} />
                                <Typography variant="body1">
                                  Duración: <b>{plan.duracion_dias} días</b>
                                </Typography>
                              </Box>

                              <Divider sx={{ my: 2 }} />

                              <List
                                dense
                                sx={{
                                  mb: 1,
                                  px: 1,
                                  '& .MuiListItem-root': {
                                    borderRadius: 1.5,
                                    transition: 'background-color .2s ease',
                                    '&:hover': {
                                      backgroundColor: theme.palette.mode === 'light'
                                        ? 'rgba(0,0,0,0.03)'
                                        : 'rgba(255,255,255,0.06)'
                                    }
                                  }
                                }}
                              >
                                {(caracteristicasPorPlan[plan.nombre] || []).map((caracteristica, index) => (
                                  <ListItem key={`${plan.id}-feat-${index}`} sx={{ py: 0.6 }}>
                                    <ListItemIcon sx={{ minWidth: 34 }}>
                                      <CheckIcon color="success" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                      primaryTypographyProps={{ variant: 'body2' }}
                                      primary={caracteristica}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </CardContent>

                            <Box sx={{ p: { xs: 2.5, md: 3 }, pt: 0 }}>
                              <Button
                                fullWidth
                                variant={isPopular ? 'contained' : 'outlined'}
                                size="large"
                                startIcon={<MonetizationOnIcon />}
                                color="primary"
                                onClick={() => handleMejorarPlan(plan)}
                                disabled={loading}
                                sx={{
                                  py: 1.4,
                                  borderRadius: 2,
                                  fontWeight: 700,
                                  letterSpacing: '.2px',
                                  transition: 'transform .15s ease, box-shadow .15s ease',
                                  '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: theme.shadows[4],
                                  }
                                }}
                              >
                                {usuarioTienePlanActivo ? 'MEJORAR PLAN' : 'SELECCIONAR PLAN'}
                              </Button>
                            </Box>
                          </Card>
                        </Fade>
                      </Grid>
                    );
                  })
                ) : (
                  <Grid item xs={12}>
                    <Fade in={fadeIn} timeout={1000}>
                      <Paper
                        elevation={0}
                        variant="outlined"
                        sx={{
                          p: { xs: 3, md: 4 },
                          borderRadius: 3,
                          textAlign: 'center',
                          transition: 'transform .2s ease, box-shadow .2s ease',
                          '&:hover': { transform: 'translateY(-2px)', boxShadow: theme.shadows[3] }
                        }}
                      >
                        <CheckCircleOutlineIcon sx={{ fontSize: '3rem', mb: 1.5, color: 'primary.main' }} />
                        <Typography variant="h5" fontWeight={800} gutterBottom color="text.primary">
                          {mostrandoClinicaPeroOculto
                            ? 'Los planes de clínica se gestionan en "Suscripción (Clínica)".'
                            : '¡Ya tienes el mejor plan disponible!'}
                        </Typography>
                        {!mostrandoClinicaPeroOculto && (
                          <Typography variant="body1" color="text.secondary">
                            Actualmente disfrutas de todas las funcionalidades premium.
                          </Typography>
                        )}
                      </Paper>
                    </Fade>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Box>
        </Fade>
      </Box>
    </Box>
  );
};

export default PlanesPage;
